import { useCallback, useEffect, useMemo } from "react";
import {
  useTwilioConversations,
  twilioConversations$,
} from "@jugl-web/rest-api/integrations/hooks/useTwilioConversations";
import { TwilioConversationModel } from "@jugl-web/rest-api/integrations/models/TwilioConversation";
import { DataLoadingWrapper } from "@jugl-web/ui-components";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useInView } from "react-intersection-observer";
import {
  CRMChannel,
  CRMConversationFilter,
  CRMConversationMode,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { TwilioConversationTile } from "../TwilioConversationTile";
import { ConversationListEmptyState } from "../ConversationListEmptyState";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useSelectedConversation } from "../../hooks/useSelectedConversation";
import { reactivateClosedConversation } from "../../../../utils/reactivateClosedConversation";

export interface TwilioConversationsProps {
  searchQuery: string;
  selectedMode: CRMConversationMode;
  selectedFilter: CRMConversationFilter;
  setSelectedFilter: (value: CRMConversationFilter) => void;
}

export const TwilioConversations: React.FC<TwilioConversationsProps> = ({
  searchQuery,
  selectedMode,
  selectedFilter,
  setSelectedFilter,
}) => {
  const { selectedConversation, selectConversation, incomingTwilioMessages$ } =
    useClientSupportPageContext();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const [fetchConversationById] =
    integrationsApi.useLazyGetTwilioConversationByIdQuery();
  const { ref: inViewRef, inView } = useInView();
  const { me } = useMe();

  // Compute filter for Twilio based on selected mode
  const computedFilter = useMemo(() => {
    if (selectedFilter !== CRMConversationFilter.active) {
      return selectedFilter;
    }

    switch (selectedMode) {
      case CRMConversationMode.managedByMe:
        return me?.id || "";
      case CRMConversationMode.waitingResponse:
      case CRMConversationMode.onAIAutoReply:
        return selectedMode;
      default:
        return CRMConversationFilter.active;
    }
  }, [selectedFilter, selectedMode, me?.id]);

  const {
    data: conversations,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore,
    retry,
    loadMore,
  } = useTwilioConversations({
    entityId,
    searchTerm: searchQuery,
    filter: computedFilter,
    pageSize: 10,
  });

  useSelectedConversation({
    channel: CRMChannel.twilio,
    selectedFilter,
    setSelectedFilter,
  });

  const prependConversationById = useCallback(
    async (convId: string) => {
      const response = await fetchConversationById({ entityId, convId });

      if (response && "data" in response && response.data) {
        twilioConversations$.next({
          type: "prepend",
          conversation: response.data,
        });
      }
    },
    [entityId, fetchConversationById]
  );

  // Handle real-time message updates
  useEffect(() => {
    const subscription = incomingTwilioMessages$.subscribe((message) => {
      const conversationExists = conversations.some(
        (c) => c.conv_id === message.conv_id
      );

      if (!conversationExists) {
        prependConversationById(message.conv_id);
        return;
      }

      // Update selected conversation if it matches
      if (selectedConversation?.conv_id === message.conv_id) {
        if (
          (selectedConversation.msg_id === message.msg_id &&
            (selectedConversation as TwilioConversationModel).payload
              .timestamp >= message.payload.timestamp) ||
          selectedConversation.msg_id !== message.msg_id
        ) {
          const updatedConversation = reactivateClosedConversation({
            ...selectedConversation,
            ...message,
          });
          twilioConversations$.next({
            type: "update",
            conversation: updatedConversation as TwilioConversationModel,
          });
          selectConversation(
            updatedConversation as TwilioConversationModel,
            true
          );
        }
      }

      // Find existing conversation and update it
      const existingConversation = conversations.find(
        (c) => c.conv_id === message.conv_id
      );

      if (
        existingConversation &&
        existingConversation.payload.timestamp <= message.payload.timestamp &&
        existingConversation.msg_id !== message.msg_id
      ) {
        const updatedConversation: TwilioConversationModel =
          reactivateClosedConversation({
            ...existingConversation,
            ...message,
          });

        twilioConversations$.next({
          type: "prepend",
          conversation: updatedConversation,
        });

        if (selectedConversation?.conv_id === updatedConversation.conv_id) {
          selectConversation(updatedConversation, true);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [
    incomingTwilioMessages$,
    conversations,
    prependConversationById,
    selectedConversation,
    selectConversation,
  ]);

  // Handle infinite scroll
  useEffect(() => {
    if (inView && hasMore && !isLoadingMore) {
      loadMore();
    }
  }, [inView, hasMore, isLoadingMore, loadMore]);

  return (
    <DataLoadingWrapper
      isError={isError}
      isLoading={isLoading}
      onRetry={retry}
      isLoadingMore={isLoadingMore}
      isLoadingMoreError={isLoadingMoreError}
    >
      {conversations.map((conversation) => (
        <TwilioConversationTile
          key={conversation.conv_id}
          conversation={conversation}
          searchQuery={searchQuery}
        />
      ))}
      {hasMore && !isLoading && !isLoadingMore && <div ref={inViewRef} />}
      <ConversationListEmptyState
        hasConversations={conversations.length > 0}
        searchQuery={searchQuery}
        selectedFilter={selectedFilter}
        selectedMode={selectedMode}
      />
    </DataLoadingWrapper>
  );
};
